import { Component, Inject, NgModule } from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule, AsyncPipe, JsonPipe } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA, MatDialogClose } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatIconModule, MatIcon } from '@angular/material/icon';
import { UntypedFormControl, UntypedFormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { combineLatest, Observable, Subject, map, startWith, switchMap, tap } from 'rxjs';
import { Catalog } from '@uw/core/models/catalog.model';
import { CatalogResponse } from '@uw/core/api/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatCheckboxModule, MatCheckbox } from '@angular/material/checkbox';
import { TwoColumnMicroLayoutModule } from '../two-column-micro-layout';
import { provinces } from '@uw/core/services/look-up.service';
import { CatalogMgmtService } from '@uw/core/services/catalog-mgmt.service';
import { DEFAULT_OPTION } from '@uw/core/constants';
import { guid } from '@uw/core/utils/util-functions';
import { TwoColumnMicroLayoutComponent, TwoColumnMicroLayoutLeftDirective, TwoColumnMicroLayoutRightDirective } from '../two-column-micro-layout/two-column-micro-layout.component';

@Component({
    selector: 'uw-catalog-detail-dialog',
    templateUrl: './catalog-detail-dialog.component.html',
    styleUrls: ['./catalog-detail-dialog.component.scss'],
    imports: [A11yModule, ReactiveFormsModule, MatIconButton, MatDialogClose, MatIcon, TwoColumnMicroLayoutComponent, TwoColumnMicroLayoutLeftDirective, TwoColumnMicroLayoutRightDirective, MatCheckbox, AsyncPipe, JsonPipe, TranslateModule]
})
export class CatalogDetailDialogComponent {

  DEFAULT_OPTION = DEFAULT_OPTION;

  private readonly _saveCatalogSubject: Subject<Catalog> = new Subject();

  private readonly _save$ = this._saveCatalogSubject.asObservable();

  readonly provinces = provinces;

  _sortOrders: number[] = [];

  get sortOrders() {
    if (this._sortOrders.length == 0) {
      for (let i = 0; i < 200; i++) {
        this._sortOrders.push(i);
      }
    }
    return this._sortOrders;
  }

  readonly form: UntypedFormGroup = new UntypedFormGroup({
    catalogItemId: new UntypedFormControl(null, []),
    catalogId: new UntypedFormControl(null, []),
    catalogTypeCode: new UntypedFormControl(null, []),
    address: new UntypedFormControl(null, [Validators.required]),
    alias: new UntypedFormControl(null, []),
    charityName: new UntypedFormControl(null, [Validators.required]),
    city: new UntypedFormControl(null, [Validators.required]),
    charitableRegistrationNumber: new UntypedFormControl(null, [Validators.required]),
    frenchName: new UntypedFormControl(null, []),
    fundKey: new UntypedFormControl(null, []),
    fundKeyGuId: new UntypedFormControl(guid(), []),
    isEnabled: new UntypedFormControl(false, []),
    isPrivate: new UntypedFormControl(false, []),
    isRevoked: new UntypedFormControl(false, []),
    keywords: new UntypedFormControl(null, []),
    parentId: new UntypedFormControl(null, []),
    parentName: new UntypedFormControl(null, [Validators.required]),
    postalCode: new UntypedFormControl(null, [Validators.required]),
    province: new UntypedFormControl("ON", [Validators.required]),
    unitedWayAgency: new UntypedFormControl(false, []),
    sortOrder: new UntypedFormControl(0, [Validators.required])
  });

  get catalogTypeControl(): UntypedFormControl {
    return this.form.get('catalogTypeCode')! as UntypedFormControl;
  }

  get catalogControl(): UntypedFormControl {
    return this.form.get('catalogId')! as UntypedFormControl;
  }

  catalogsByType$!: Observable<CatalogResponse[]>;

  readonly vm$ = combineLatest([
    this._catalogMgmtService.getCatalogTypes(),
    this._save$.pipe(
      switchMap((catalogItem: any) => {

        const obs$ = catalogItem.catalogItemId ? this._catalogMgmtService.update([catalogItem]) : this._catalogMgmtService.createCatalogItem(catalogItem);

        return obs$.pipe(
          map(catalog => {
            this._dialogRef.close(catalog);
          })
        )
      }),
      startWith(null)
    )
  ]).pipe(
    map(([catalogTypes]) => ({ catalogTypes, currentLang: this._translateService.currentLang }))
  )

  constructor(
    @Inject(MAT_DIALOG_DATA) _catalog: Catalog | null,
    private readonly _translateService: TranslateService,
    private readonly _catalogMgmtService: CatalogMgmtService,
    private readonly _dialogRef: MatDialogRef<CatalogDetailDialogComponent>
  ) {
    this.form.patchValue(_catalog || {});

    this.catalogsByType$ = this.form.get('catalogTypeCode')!.valueChanges.pipe(
      startWith(this.catalogTypeControl.value),
      switchMap(catalogType => this._catalogMgmtService.getCatalogsByCatalogType(catalogType))
    )
  }

  handleSave(catalog: Catalog) {
    this._saveCatalogSubject.next(catalog);
  }
}

@NgModule({
    exports: [
        CatalogDetailDialogComponent
    ],
    imports: [
        A11yModule,
        CommonModule,
        MatDialogModule,
        MatFormFieldModule,
        MatButtonModule,
        MatIconModule,
        ReactiveFormsModule,
        TranslateModule,
        MatCheckboxModule,
        TwoColumnMicroLayoutModule,
        CatalogDetailDialogComponent
    ]
})
export class CatalogDetailDialogModule { }
